// src/Pages/Setup/SetupWizardBase.jsx
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import NeonButton from "../../components/ResponsiveButton/NeonButton.jsx";
import ProfileImageUploader from "../../components/ProfileImageUploader/ProfileImageUploader.jsx";
import { api } from "../../services/api";
import { useAuth } from "../../context/AuthContext.jsx";
import { bandChecklist } from "../../utils/profileChecklist";
import municipalitiesFI from "../../data/municipalities.fi.json";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import { GMAPS_LOADER_OPTIONS, USE_GMAPS } from "../../components/utils/gmapsLoader.js";
import { PageOverlayLoading } from "../../components/Loading/Spinner.jsx";
import { nav } from "../../utils/nav";
import { pickMunicipalityFromComponents, resolveMunicipality } from "../../utils/municipality.js";

// ---- apurit (sama kuin aiemmin) ----
const ORIGIN_FROM_API = (() => {
  try {
    const base =
      typeof window !== "undefined" ? window.location.origin : "https://localhost";
    return new URL(import.meta.env?.VITE_API_BASE ?? base, base).origin;
  } catch {
    return typeof window !== "undefined" ? window.location.origin : "";
  }
})();

function normalizeProfileForSave(v) {
  if (!v) return v;
  try {
    const u = new URL(v, ORIGIN_FROM_API);
    return u.origin === ORIGIN_FROM_API ? `${u.pathname}${u.search}${u.hash}` : v;
  } catch {
    return v;
  }
}

const MAX_DESC = 1000;
const ARTIST_TYPES = ["Bilebändi", "Trubaduuri", "Duo", "DJ", "Sooloartisti"];

const INITIAL_FORM = {
  name: "",
  slug: "",
  artist_type_name: "",
  location: "",
  address: "",
  description_short: "",
  profile_image: "",
  lat: null,
  lng: null,
  google_place_id: "",
};

const MUNICIPALITIES = Array.from(
  new Set(municipalitiesFI.flatMap((r) => r.kunnat.map(String)))
).sort((a, b) => a.localeCompare(b, "fi"));

function slugify(input = "") {
  return String(input)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function examplePitches(type = "Bilebändi", location = "") {
  switch (type) {
    case "Sooloartisti":
      return [
        "Hääviulisti, joka soittaa seremoniamusiikin, cocktail-hetken ja illallisen taustalle. Ohjelmistossa on klassista, kevyttä viihdemusiikkia ja hääpareille tärkeitä kappaleita – myös toivebiisit onnistuvat. Soitto toimii sekä akustisena että kevyesti vahvistettuna, tilaan ja tunnelmaan sopivalla volyymilla.",
        "Naislaulaja taustabändillä, joka nostaa illan bilevaihteelle. Setit koostuvat hää- ja yritysbileiden tutuista hiteistä, medley-otteella ja vahvoilla stemmoilla. Sovitaan etukäteen toivekappaleet, mahdollinen häävalssi sekä illan kaari – bändi tuo paikalle äänentoiston ja perusvalot, ja pitää tanssilattian liikkeessä.",
        "Rumpali, joka tarjoaa energisen drumshow’n esimerkiksi avajaisiin, gaalailtoihin tai bileiden show-hetkeen. Lyhyt, tiivis setti modernilla perkussiosetillä, sampleilla ja efekteillä – voidaan toteuttaa myös DJ:n tai bändin kanssa yhteistyössä. Show on visuaalinen, rytminen ja rakennettu selkeäksi huippukohdaksi iltaan."
      ];

    case "Trubaduuri":
      return [
        "Lämminhenkistä akustista livemusaa juhliin ja yritystilaisuuksiin. Laulava trubaduuri + loopperi luovat intiimin, tarvittaessa isomman soundin. Ohjelmisto kulkee klassikoista tuoreeseen poppiin, volyymi tilanteen mukaan. Pystytys nopea, tilantarve pieni.",
        "Akustinen trubaduurikeikka, joka skaalautuu illallisen taustasta yhteislauluun. Repertuaari: kotimaiset ja kansainväliset hitit eri vuosikymmeniltä, toiveet huomioiden. Äänentoisto sisältyy, volyymi sovitetaan tilaan. Pystytys 15–20 min, tilantarve noin 2×2 m ja yksi maadoitettu pistorasia.",
        "Yritys- ja yksityistilaisuuksiin sopiva akustinen setti: laulava trubaduuri rakentaa täyteläisen maltillisen äänimaiseman. Soitan tuttuja hittejä ja uutta poppia, taustaksi tai nostattamaan tunnelmaa. Suunnitellaan aikataulu ja toivebiisit etukäteen."
      ];

    case "Duo":
      return [
        "Kaksihenkinen kokoonpano, joka mahtuu pieneenkin tilaan mutta kuulostaa isolta bändiltä. Laulu + kitara/keyboard, tarvittaessa taustaraidat ja stemmat. Sopii häihin, synttäreille ja yritystilaisuuksiin, kun haluatte elävän musiikin ilman täyttä bilebändiä.",
        "Monipuolinen duo, joka hoitaa sekä illallismusiikin että myöhemmin tanssittavan setin. Ohjelmistossa kotimaiset ja ulkomaiset hitit usealta vuosikymmeneltä, toiveet huomioidaan. Tekniikka kulkee mukana ja volyymi sovitetaan tilaan.",
        "Duo, joka toimii myös ahtaissa ravintoloissa ja yksityiskodeissa. Nopea pystytys, siisti lavailme ja roudaus omasta takaa. Äänentoisto ja perusvalot sisältyvät, tarvittaessa lisävalot ja juonnot."
      ];

    case "DJ":
      return [
        "DJ, joka lukee yleisöä ja pitää tanssilattian liikkeessä. Kaupallinen pop, disco ja house sulavilla miksauksilla, tempossa ja dynamiikassa tilanteen mukaan. Toiveet huomioidaan, illan kaari rakennetaan fiksusti. Mikrofoni juontoihin, nopea vaihto ja siisti toteutus.",
        "Häät, firmanbileet tai klubi – räätälöidään teille sopiva setti. Laadukas äänentoisto, näyttävät ja tyylikkäät valot. Saatte toive- ja “ei soiteta” -listat, tarvittaessa juonnan. Tekniikka kulkee mukana, rakennus 30–45 min. Volyymi säädetään tilaan mukaan.",
        "Kevyt tai täysi show tarpeen mukaan. Nopea vaihtorytmi, saumattomat siirtymät ja hyvä flow koko illan. Valo- ja tarvittaessa haze-ratkaisut tilan mukaan, tanssilattian kohdevalot saatavilla. Siisti kaapelointi ja turvallinen toteutus. Soveltuu 50–300 hengelle; speksit ja toivebiisit sovitaan etukäteen."
      ];

    case "Taikuri":
      return [
        "Moderni viihdetaikuri, joka yhdistää lähitaikuuden ja lavashown rennolla otteella. Esitys sopii sekä yritystilaisuuksiin että yksityisjuhliin ja voidaan räätälöidä illan aikatauluun. Painotan vuorovaikutusta yleisön kanssa ja pidän huolen, että homma pysyy tyylikkäänä mutta hauskana.",
        "Taikashow, joka toimii sekä pienelle porukalle että isommalle yleisölle. Mukana visuaalisia temppuja, huumoria ja osallistavaa taikuutta. Voin esiintyä lavalta tai kiertää pöydästä pöytään. Tarvittaessa hoidan myös juonnon ja ohjelman rungon yhteen.",
        "Lähitaikuutta aivan yleisön nenän edessä – kortit, kolikot ja muut arjen esineet muuttuvat käsissäsi. Tämä toimii täydellisesti cocktail-tilaisuuksissa, messuilla ja illallisten yhteydessä. Sovitaan etukäteen, millaista fiilistä ja kestoa tilaisuuteenne haetaan."
      ];

    default: // Bilebändi
      return [
        "Moderni bilebändi, joka yhdistää tuoreet hitit ja ajattomat klassikot energiseen, tanssitettavaan medley-otteeseen. Sopii häihin, gaaloihin ja yritysbileisiin. Kaksi laulajaa, tiukka komppi ja toiveiden joustava huomiointi. Volyymi tilaan sopivaksi.",
        "Nelihenkinen kokoonpano (laulu/kitara, koskettimet, basso, rummut) vahvoilla stemmoilla ja modernilla soundilla. kasaus 30–60 min, siisti lavailme ja turvallinen toteutus. Perusvalot sisältyvät, lisäshow mahdollista (moving headit, haze). Lavakoko skaalautuu pikkuravintolasta juhlasaliin. Keikat koko Suomessa jaselkeä aikataulu.",
        "Iltaan rakennetaan kaari: alkuun maltillinen illallissetti, lopussa täysi tanssilattia. Luemme yleisöä ja yhdistämme sujuvasti 90-luvun ja tämän päivän hitit, suomeksi ja englanniksi. In-ear-kuuntelu ja hiljainen lava pitävät volyymin miellyttävänä myös rajoittimien kanssa. Tekniikkarideri saatavilla. Taukomusiikki ja tarvittaessa juonnot hoidetaan myös."
      ];
  }
}

export default function SetupWizardBase({ variant }) {
  const isAdd = variant === "add";
  const showUserStep = !isAdd; // Onboarding näyttää step 0:n
  const initialStep = showUserStep ? 0 : 1;

  const navigate = useNavigate();
  const { user } = useAuth();

    // ⬇️ LISÄÄ TÄMÄ GUARD HETI navigate/user -rivien JÄLKEEN
    useEffect(() => {
    if (!user) return;
    if (!user.accepted_terms_at || !user.accepted_privacy_at) {
        navigate("/onboarding", {
        replace: true,
        state: { from: { pathname: "/setup" } },
        });
    }
    }, [user, navigate]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const [form, setForm] = useState(INITIAL_FORM);
  const [existingCount, setExistingCount] = useState(0);

  const { isLoaded: gIsLoaded, loadError: gLoadError } = useJsApiLoader(GMAPS_LOADER_OPTIONS);
  const hasPlaces = typeof window !== "undefined" && !!window.google?.maps?.places;
  const mapsLoaded = hasPlaces || (USE_GMAPS && gIsLoaded);
  const mapsError = gLoadError || null;

  const addressACRef = useRef(null);

  const onSetupAddressPicked = () => {
  const ac = addressACRef.current;
  const place = ac?.getPlace?.();
  if (!place) return;

  const comps = Array.isArray(place.address_components)
    ? place.address_components
    : [];

  // 1) ensisijaisesti: Google-komponentit (postinumero, locality jne.)
  let muni = pickMunicipalityFromComponents(comps);

  // 2) fallback: koko osoiterivi / nimi, jos komponentit ei riitä
  if (!muni) {
    const addrText = place.formatted_address || place.name || "";
    muni = resolveMunicipality(addrText);
  }

  setForm((f) => ({
    ...f,
    address: place.formatted_address || place.name || f.address,
    location: muni || f.location, // säilytä edellinen jos ei löydy
    lat: place.geometry?.location?.lat?.() ?? f.lat,
    lng: place.geometry?.location?.lng?.() ?? f.lng,
    google_place_id: place.place_id || f.google_place_id,
  }));
};


  // Step 0: käyttäjän perustiedot (vain onboarding)
  const [userForm, setUserForm] = useState({ display_name: "", phone: "" });
  const [userPhoneError, setUserPhoneError] = useState("");
  const isFormValid = useMemo(() => {
    if (!showUserStep) return true;
    const nameOk = Boolean(userForm.display_name?.trim()?.length >= 2);
    const phoneTrim = String(userForm.phone || "").trim();
    const phoneOk = Boolean(phoneTrim) && /^[0-9()+\-\s]{7,50}$/.test(phoneTrim) && !userPhoneError;
    return nameOk && phoneOk;
  }, [showUserStep, userForm.display_name, userForm.phone, userPhoneError]);

  const [step, setStep] = useState(initialStep);

  // Tulkitse soolo vs ryhmä tyypin perusteella
  const isSoloType = ["Trubaduuri", "DJ", "Sooloartisti", "Taikuri"].includes(
    form.artist_type_name
  );

  // Step 2 -introteksti tyypin mukaan
  const descIntro = useMemo(() => {
    switch (form.artist_type_name) {
      case "Sooloartisti":
      case "Trubaduuri":
      case "DJ":
      case "Taikuri":
        return "Kirjoita 2–4 virkettä, jotka kertovat selkeästi kuka olet ja miksi juuri sinut kannattaa buukata.";
      case "Duo":
        return "Kirjoita 2–4 virkettä, jotka kertovat selkeästi keitä olette ja miksi juuri teidät kannattaa buukata.";
      case "Bilebändi":
      default:
        return "Kirjoita 2–4 virkettä, jotka kertovat selkeästi, millainen esiintyjäporukka olette ja miksi juuri teidät kannattaa buukata.";
    }
  }, [form.artist_type_name]);

  // Bullet-kohdat: soolo vs ryhmä
  const descBullets = useMemo(() => {
    if (isSoloType) {
      return [
        "Mitä tarjoat ja missä tilanteissa olet parhaimmillasi.",
        "Perusteltu syy valita juuri sinut (uniikki vahvuus, kokemus, tyyli, show).",
      ];
    }
    return [
      "Mitä tarjoatte ja missä tilanteissa olette parhaimmillanne.",
      "Perusteltu syy valita juuri teidät (uniikki vahvuus, kokemus, kokoonpano, show).",
    ];
  }, [isSoloType]);


// Lataa käyttäjä + bändimäärä
useEffect(() => {
let cancelled = false;

(async () => {
    try {
    // 1) käyttäjä
    let u = {};
    try {
        const meRes = await api.auth.me();
        u = meRes?.user || meRes || {};
        if (!cancelled) {
        setUserForm({
            display_name: String(u.display_name || user?.display_name || "").trim(),
            phone: String(u.phone || "").trim(),
        });
        }
    } catch {
        /* ei kriittinen tässä */
    }

    // 2) omat bändit listaan (lisäkortti-infoksi)
    let items = [];
    try {
        const res = await api.auth.meBands().catch(() => ({}));
        items = Array.isArray(res?.bands)
        ? res.bands
        : Array.isArray(res?.items)
        ? res.items
        : Array.isArray(res)
        ? res
        : [];
    } catch {}

    if (cancelled) return;
    setExistingCount(items.length);

    // 3) Onboarding: jos käyttäjällä jo nimi+puhelin, hypätään suoraan step 1
    if (!isAdd) {
        const dn = String(u.display_name || user?.display_name || "").trim();
        const ph = String(u.phone || "").trim();
        const hasBasics = dn.length >= 2 && /^[0-9()+\-\s]{7,50}$/.test(ph);
        if (hasBasics) setStep(1);
    }
    } catch {
    if (!cancelled) setErr("Tietojen haku epäonnistui.");
    } finally {
    if (!cancelled) setLoading(false);
    }
})();

return () => {
    cancelled = true;
};
// eslint-disable-next-line react-hooks/exhaustive-deps
}, []); // boot kerran

  // Live-availability (UI)
  const [nameCheck, setNameCheck] = useState({ status: "idle", matches: [] });
  const [slugCheck, setSlugCheck] = useState({ status: "idle" });

  useEffect(() => {
    const name = form.name.trim();
    if (!name) {
      setNameCheck({ status: "idle", matches: [] });
      return;
    }
    setNameCheck((s) => ({ ...s, status: "checking" }));
    const t = setTimeout(async () => {
      try {
        const data = await api.bands.availability({ name });
        setNameCheck({
          status: data?.name?.available ? "available" : "taken",
          matches: data?.name?.matches || [],
        });
      } catch {
        setNameCheck({ status: "idle", matches: [] });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [form.name]);

  useEffect(() => {
    const slug = form.slug.trim();
    if (!slug) {
      setSlugCheck({ status: "idle" });
      return;
    }
    setSlugCheck({ status: "checking" });
    const t = setTimeout(async () => {
      try {
        const data = await api.bands.availability({ slug });
        setSlugCheck({ status: data?.slug?.available ? "available" : "taken" });
      } catch {
        setSlugCheck({ status: "idle" });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [form.slug]);

  // Checklist
  const checklist = useMemo(() => {
    const fakeBand = {
      name: form.name,
      slug: form.slug,
      artist_type: form.artist_type_name,
      location: form.location,
      description: form.description_short,
      profile_image: form.profile_image,
    };
    return bandChecklist(fakeBand);
  }, [form]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({
      ...f,
      [name]: name === "description_short" ? value.slice(0, MAX_DESC) : value,
    }));
  };

  async function saveUserBasics() {
    if (!showUserStep) return true;
    const dn = userForm.display_name.trim();
    const ph = userForm.phone.trim();
    if (!dn) {
      toast.error("Lisää koko nimesi tai näyttönimi.");
      return false;
    }
    if (!ph) {
      setUserPhoneError("Puhelinnumero on pakollinen.");
      toast.error("Lisää puhelinnumero.");
      return false;
    }
    if (!/^[0-9()+\-\s]{7,50}$/.test(ph)) {
      setUserPhoneError("Syötä kelvollinen numero (vain numerot, välilyönnit ja +()-).");
      toast.error("Hups! 📞 Numero ei toimi – korjaa muoto (esim. +358 40 123 4567).");
      return false;
    }
    try {
      if (typeof api.auth.updateMe === "function") {
        await api.auth.updateMe({ display_name: dn, phone: ph });
      }
      return true;
    } catch (e) {
      if (e?.status === 404 || e?.status === 405) {
        toast.info("Käyttäjäprofiilin päivitys ei ole käytössä tässä ympäristössä. Jatketaan.", {
          containerId: "global",
        });
        return true;
      }
      toast.error("Käyttäjätietojen tallennus epäonnistui.");
      return false;
    }
  }

  async function finalizeCreate() {
    setSaving(true);
    setErr("");

    const wanted = form.artist_type_name || ARTIST_TYPES[0];
    let typePayload = { artist_type_name: wanted };

    // palvelimen artistityypit (valinnainen)
    try {
      const at = await api.artistTypes().catch(() => []);
      if (Array.isArray(at) && at.length > 0) {
        const norm = (s) => String(s || "").toLowerCase();
        const found = at.find((t) => norm(t?.name ?? t) === norm(wanted)) || at[0];
        if (found && typeof found === "object" && Number.isFinite(+found.id)) {
          typePayload = {
            artist_type_id: Number(found.id),
            artist_type_name: String(found.name || wanted),
          };
        }
      }
    } catch {}

    const chosenProfile = normalizeProfileForSave((form.profile_image || "").trim());
    const payload = {
      name: form.name.trim(),
      slug: form.slug.trim(),
      location: form.location.trim(),
      address: form.address.trim(),
      description_short: form.description_short.trim(),
      profile_image: chosenProfile,
      ...(chosenProfile ? { placeholder_image: chosenProfile } : {}),
      ...typePayload,
      ...(Number.isFinite(form.lat) ? { lat: Number(form.lat) } : {}),
      ...(Number.isFinite(form.lng) ? { lng: Number(form.lng) } : {}),
      ...(form.google_place_id ? { google_place_id: form.google_place_id } : {}),
    };

    try {
      // tarkista saatavuus
      try {
        const avail = await api.bands.availability({ slug: payload.slug, name: payload.name });
        if (avail?.slug?.available === false) {
          toast.error("Slug on jo varattu. Valitse toinen.", { containerId: "global" });
          setStep(1);
          setSaving(false);
          return;
        }
        if (avail?.name?.available === false) {
          toast.error("Nimi on jo käytössä. Valitse toinen.", { containerId: "global" });
          setStep(1);
          setSaving(false);
          return;
        }
      } catch {}

        // fallback: löytyykö samalla slugilla valmiiksi
        try {
        const exists = await api.bandByIdOrSlug(payload.slug);
        const existingBand = exists?.band || exists;
        if (existingBand?.id) {
            toast.info("Tämä slug on jo käytössä – avataan olemassa oleva kortti.", {
            containerId: "global",
            });

            const targetId = existingBand.slug || existingBand.id;
            nav.toDashboardBand(navigate, targetId, "basic", true);

            setSaving(false);
            return;
        }
        } catch {}


      // luonti
      const res = await api.createBand(payload);
      const id = res?.band?.id ?? res?.id;
      const createdSlug = res?.band?.slug ?? res?.slug ?? payload.slug;
      if (!id) throw new Error("Luotu ID puuttuu backendin vastauksesta");

        toast.success("Esiintyjäprofiili luotu! 🎉", { containerId: "global" });
        const idOrSlug = createdSlug || id;

        // (valinnainen) lämmitä oma bändilista taustalla
        Promise.resolve().then(() => { try { api.auth.meBands(); } catch {} });

        nav.afterSetupCreated(navigate, String(idOrSlug));
        return; // älä jatka enää finalizeCreate:ssa

    } catch (e) {
      if (e?.status === 409) {
        const code = e?.payload?.error || e?.payload?.code;
        toast.error(
          code === "name_taken"
            ? "Nimi on jo käytössä."
            : code === "slug_taken"
            ? "Slug on jo varattu."
            : "Nimi tai slug on varattu.",
          { containerId: "global" }
        );
        setStep(1);
      } else {
        const msg = e?.payload?.message || e?.message || "Luonti epäonnistui.";
        setErr(msg);
        toast.error(msg, { containerId: "global" });
      }
    } finally {
      setSaving(false);
    }
  }

  const next = async () => {
    if (step === 0) {
      const userOk = await saveUserBasics();
      if (!userOk) return;
      setStep(1);
      return;
    }
    if (step === 1) {
      if (!form.name.trim()) {
        toast.error("Lisää esiintyjän nimi.");
        return;
      }
      const ensuredSlug = (form.slug || slugify(form.name)).trim();
      if (!ensuredSlug) {
        toast.error("Lisää slug (osoitteessa).");
        return;
      }
      if (!/^[a-z0-9-]+$/.test(ensuredSlug)) {
        toast.error("Slugissa sallitaan vain pienet kirjaimet, numerot ja väliviiva (-).");
        return;
      }
      if (ensuredSlug !== form.slug) setForm((f) => ({ ...f, slug: ensuredSlug }));
      if (!form.address.trim()) {
        toast.error("Lisää katuosoitteesi.");
        return;
      }
      if (!form.artist_type_name.trim()) {
        toast.error("Lisää esiintyjätyyppi.");
        return;
      }
      if (mapsLoaded && !mapsError && !form.google_place_id) {
        toast.error("Valitse osoite pudotusvalikosta, jotta paikkakunta tallentuu.");
        return;
      }
      if (!form.location.trim()) {
        toast.error("Paikkakunta puuttuu. Valitse osoite uudelleen tai täytä paikkakunta käsin.");
        return;
      }
      if (ensuredSlug === form.slug && slugCheck.status === "taken") {
        toast.error("Slug on jo varattu. Kokeile toista.");
        return;
      }
      setStep(2);
      return;
    }
    if (step === 2) {
      if (!form.description_short.trim()) {
        toast.error("Lisää lyhyt kuvaus.");
        return;
      }
      setStep(3);
      return;
    }
    if (step === 3) {
      if (!form.profile_image?.trim()) {
        toast.error("Lisää profiilikuva.");
        return;
      }
      setStep(4);
      return;
    }
    if (!checklist.ok) {
      toast.error("Täydennä puuttuvat kohdat.");
      return;
    }
    await finalizeCreate();
  };

  const prev = () => setStep((s) => Math.max(0, s - 1));

const closeWizard = useCallback(() => {
  // Aina tiliasetuksiin, jotta kortin voi aloittaa uudelleen
  nav.toDashboardUser(navigate, true);
}, [navigate]);


  // RENDER
  if (loading) {
    return (
      <>
        <PageOverlayLoading label="Ladataan…" />
        {USE_GMAPS && !gIsLoaded && !mapsError && (
          <div className="sr-only">Ladataan Google Places -kirjastoa…</div>
        )}
        {USE_GMAPS && mapsError && (
          <div className="sr-only">
            Google Maps -kirjasto ei latautunut. Tarkista API-avain, laskutus ja referrer-rajaukset.
          </div>
        )}
      </>
    );
  }

  const isAddingAnother = existingCount > 0;
  const nameBadge =
    nameCheck.status === "checking"
      ? "Tarkistetaan…"
      : nameCheck.status === "available"
      ? "✓ Nimi on vapaa"
      : nameCheck.status === "taken"
      ? "Lavalla on jo tämä nimi 🎤 Valitse toinen"
      : "";

  const slugBadge =
    slugCheck.status === "checking"
      ? "Tarkistetaan…"
      : slugCheck.status === "available"
      ? "Slug on vapaa ✓"
      : slugCheck.status === "taken"
      ? "⚠️ Slug on jo varattu. Kokeile toista."
      : "";

  const disableNext = saving || (step === 0 ? !isFormValid : false);

  const headline = isAdd
    ? "Luodaan uusi esiintyjäkortti"
    : step === 0
    ? "Mukava saada sinut mukaan – luodaan tili."
    : "Luodaan esiintyjäprofiili";

  const subline = isAdd
    ? "Käyttäjätilisi on nyt kunnossa. Muista lisätä myös puhelinnumerosi. Jatketaan esiintyjän tietojen täytöllä."
    : step === 0
    ? "Ensin luodaan käyttäjätili kirjautumista ja hallintaa varten. Sen jälkeen tehdään esiintyjäprofiili."
    : "Käyttäjätili on nyt kunnossa. Täytetään esiintyjäprofiilin perustiedot.";

  const STEPS = showUserStep
    ? ["Käyttäjätili", "Esiintyjän tiedot", "Kuvaus", "Profiilikuva", "Tarkistus & valmis"]
    : ["Esiintyjän tiedot", "Kuvaus", "Profiilikuva", "Tarkistus & valmis"];

  const visibleIndex = Math.min(STEPS.length - 1, showUserStep ? step : Math.max(0, step - 1));
  const showBack = !(isAdd && step === 1);

  return (
    <div className="min-h-screen bg-lemon/60 dark:bg-black/40">
      <div className="mx-auto max-w-3xl px-4 pt-24 pb-16">
        <header className="mb-6 flex items-start justify-between gap-3">
          <div>
            <h1 className="font-customFont2 dark:text-white/90 text-black/90 text-2xl font-semibold">
              {headline}
            </h1>
            <p className="dark:text-white/80 text-black/80 text-xs md:text-md">{subline}</p>
          </div>
          <NeonButton
            type="button"
            variant="primary"
            className="scale-[0.80] md:scale-100 font-semibold"
            size="sm"
            onClick={closeWizard}
            aria-label="Sulje ohjattu toiminto"
          >
            Sulje
          </NeonButton>
        </header>

        <ol className="flex flex-wrap items-center gap-2 mb-6 text-sm">
          {STEPS.map((label, i) => (
            <li key={label} className="flex items-center gap-2">
              <span
                className={`h-6 w-6 grid place-items-center rounded-full text-xs ${
                  i <= visibleIndex
                    ? "dark:bg-electric/80 bg-pink dark:text-white text-black font-semibold"
                    : "bg-black/10 dark:bg-white/30 text-black/70 dark:text-white/70"
                }`}
                aria-current={i === visibleIndex ? "step" : undefined}
              >
                {i + 1}
              </span>
              <span className="dark:text-white/80 text-black/80 text-xs">{label}</span>
              {i < STEPS.length - 1 && <span className="w-8 h-px bg-black/80 dark:bg-white/80" />}
            </li>
          ))}
        </ol>

        <div className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/80 dark:bg-black/60 backdrop-blur-md shadow-lg p-4 sm:p-5">
          {err && (
            <div className="mb-4 rounded-md bg-red-100/70 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              {err}
            </div>
          )}

          {/* STEP 0: vain onboardingissa */}
          {showUserStep && step === 0 && (
            <section>
              <h2 className="font-customFont2 dark:text-white/90 text-black/90 text-2xl font-semibold pb-2">
                Käyttäjän perustiedot
              </h2>
              <div className="grid grid-cols-1 gap-4">
                <Field label="Nimesi *">
                  <input
                    name="display_name"
                    value={userForm.display_name}
                    onChange={(e) => setUserForm((u) => ({ ...u, display_name: e.target.value }))}
                    className="w-full h-11 rounded-lg px-3 bg-white dark:bg-[#0b0f1a] text-black/80 dark:text-white/90 border border-black/10 dark:border-white/10"
                    placeholder="Etunimi Sukunimi"
                    required
                    aria-required="true"
                    minLength={2}
                    maxLength={120}
                  />
                </Field>

                <Field label="Puhelinnumero *">
                  <div>
                    <input
                      name="phone"
                      value={userForm.phone}
                      onChange={(e) => {
                        const v = e.target.value;
                        setUserForm((u) => ({ ...u, phone: v }));
                        setUserPhoneError(
                          v.trim().length === 0
                            ? "Puhelinnumero vaaditaan."
                            : /^[0-9()+\-\s]{7,50}$/.test(v)
                            ? ""
                            : "Syötä kelvollinen numero (vain numerot, välilyönnit ja + ( ) - )."
                        );
                      }}
                      className="w-full h-11 rounded-lg px-3 bg-white dark:bg-[#0b0f1a] text-black/80 dark:text-white/90 border border-black/10 dark:border-white/10"
                      placeholder="+358 40 123 4567"
                      required
                      aria-required="true"
                      aria-invalid={Boolean(userPhoneError)}
                      pattern="^[0-9()+\-\s]{7,50}$"
                      inputMode="tel"
                      autoComplete="tel"
                    />
                    {userPhoneError && (
                      <p className="mt-1 text-xs text-red-600 dark:text-red-400">{userPhoneError}</p>
                    )}
                  </div>
                </Field>
              </div>
            </section>
          )}

          {/* STEP 1: perustiedot */}
          {step === 1 && (
            <section>
              <h2 className="font-customFont2 dark:text-white/90 text-black/90 text-2xl font-semibold">
                Esiintyjän perustiedot
              </h2>
              <p className="dark:text-white/80 text-black/80 font-customFont1 text-xs opacity-60 mb-3">
                Kaikki kentät ovat pakollisia — näillä rakennetaan profiilin selkäranka.
              </p>

              <div className="grid grid-cols-1 gap-4">
                <Field label={`Esiintyjän nimi * ${nameBadge ? "• " + nameBadge : ""}`}>
                  <input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    onBlur={() => setForm((f) => ({ ...f, slug: f.slug || slugify(f.name) }))}
                    className="w-full h-11 rounded-lg px-3 bg-white dark:bg-[#0b0f1a] text-black/80 dark:text-white/90 border border-black/10 dark:border-white/10 text-xs md:text-lg"
                    placeholder="Esim. Bilebändi Turposirpa 5000"
                    required
                    aria-required="true"
                    minLength={2}
                  />
                  <small className="block dark:text-white/80 text-black/80 font-customFont1 text-xs md:text-sm opacity-80 mt-1">
                    Tämä nimi näkyy hauissa ja profiilissa. Slug päivittyy nimen perusteella.
                  </small>
                </Field>

                <Field label={`Slug (osoitteen loppu) * ${slugBadge ? "• " + slugBadge : ""}`}>
                  <input
                    name="slug"
                    value={form.slug}
                    onChange={(e) => setForm((f) => ({ ...f, slug: slugify(e.target.value || f.name) }))}
                    className="w-full h-11 rounded-lg px-3 bg-white dark:bg-[#0b0f1a] text-black/80 dark:text-white/90 border border-black/10 dark:border-white/10 text-xs md:text-lg"
                    placeholder={slugify(form.name)}
                    required
                    aria-required="true"
                    pattern="[a-z0-9-]+"
                    title="Käytä vain pieniä kirjaimia, numeroita ja väliviivaa (-)."
                  />
                  <small className="block dark:text-white/80 text-black/80 font-customFont1 text-xs md:text-ms opacity-80 mt-1">
                    Muodostaa profiilin URL-osoitteen lopun (esim. <i>/esiintyjat/Turposirpa-5000</i>).
                  </small>
                </Field>

                <Field label="Osoite *">
                  {mapsLoaded && !mapsError ? (
                    <Autocomplete
                      onLoad={(ac) => (addressACRef.current = ac)}
                      onPlaceChanged={onSetupAddressPicked}
                      options={{
                        componentRestrictions: { country: "fi" },
                        types: ["address"],
                        fields: ["place_id", "formatted_address", "geometry", "address_components", "name"],
                      }}
                    >
                      <input
                        id="setup-address"
                        name="bbx_address"
                        value={form.address}
                        onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.preventDefault();
                        }}
                        className="w-full h-11 rounded-lg px-3 bg-white dark:bg-[#0b0f1a] text-black/80 dark:text-white/90 border border-black/10 dark:border-white/10 text-xs md:text-lg"
                        placeholder="Kirjoita osoite ja valitse…"
                        autoComplete="new-password"
                        autoCapitalize="off"
                        autoCorrect="off"
                        spellCheck={false}
                        required
                        aria-required="true"
                      />
                    </Autocomplete>
                  ) : (
                    <input
                      name="address"
                      value={form.address}
                      onChange={onChange}
                      className="w-full h-11 rounded-lg px-3 bg-white dark:bg-[#0b0f1a] text-black/80 dark:text-white/90 border border-black/10 dark:border-white/10 text-xs md:text-lg"
                      placeholder="Esim. Mannerheimintie 10, Helsinki"
                      required
                      aria-required="true"
                    />
                  )}
                </Field>

                <Field label="Esiintyjätyyppi *">
                  <select
                    name="artist_type_name"
                    value={ARTIST_TYPES.includes(form.artist_type_name) ? form.artist_type_name : ""}
                    onChange={onChange}
                    className="w-full h-11 rounded-lg px-3 bg-white dark:bg-[#0b0f1a] text-black/80 dark:text-white/90 border border-black/10 dark:border-white/10 text-xs md:text-lg"
                    required
                    aria-required="true"
                  >
                    <option value="" disabled>
                      Valitse…
                    </option>
                    {ARTIST_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </Field>

                {mapsLoaded && !mapsError ? (
                  <Field label="Paikkakunta (poimitaan osoitteesta)">
                    <div className="h-11 flex items-center px-3 rounded-lg bg-black/5 dark:bg-white/10 text-black/80 dark:text-white/80 text-xs md:text-lg">
                      {form.location || "— Valitse osoite yltä —"}
                    </div>
                    <input type="hidden" name="location" value={form.location || ""} />
                  </Field>
                ) : (
                  <Field label="Paikkakunta *">
                    <select
                      name="location"
                      value={MUNICIPALITIES.includes(form.location) ? form.location : ""}
                      onChange={onChange}
                      required
                      className="w-full h-11 rounded-lg px-3 bg-white dark:bg-[#0b0f1a] text-black/80 dark:text-white/90 border border-black/10 dark:border-white/10 text-xs md:text-lg"
                    >
                      <option value="" disabled>
                        Valitse paikkakunta…
                      </option>
                      {MUNICIPALITIES.map((k) => (
                        <option key={k} value={k}>
                          {k}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}
              </div>
            </section>
          )}

          {/* STEP 2: Kuvaus */}
          {step === 2 && (
            <section>
              <h2 className="font-customFont2 dark:text-white/90 text-black/90 text-2xl font-semibold mb-3">
                Esittely
              </h2>

              <div className="dark:text-white/80 text-black/80 font-customFont1 text-xs md:text-lg opacity-80 mb-3">
                <p>{descIntro}</p>
                <ul className="list-disc pl-5 mt-1 space-y-1">
                  {descBullets.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
                <div className="mt-2 opacity-80">
                  <b>Maksimi 1000 merkkiä.</b> Hyvä mitta on usein{" "}
                  <b>200–800 merkkiä</b> – napakka ja konkreettinen.
                </div>
              </div>

              <div className="mb-3 rounded-lg border border-black/10 dark:border-white/10 bg-white/70 dark:bg-black/40 p-3">
                <div className="opacity-70 mb-2 dark:text-white/80 text-black/80 font-customFont1 md:text-lg font-semibold">
                  Apua kirjoittamiseen: valitse nopeasti sopiva aloitus (
                  {form.artist_type_name || "esiintyjätyyppi"}).
                </div>
                <ul className="space-y-2">
                  {examplePitches(form.artist_type_name, form.location).map((ex, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="mt-1 dark:text-white/80 text-black/80 font-customFont1 text-xs md:text-lg">
                        •
                      </span>
                      <div className="flex-1 dark:text-white/80 text-black/80 font-customFont1 md:text-lg">
                        {ex}
                      </div>
                      <button
                        type="button"
                        className="shrink-0 underline opacity-80 hover:opacity-100 dark:text-white/80 text-black/80 md:text-lg"
                        onClick={() =>
                          setForm((f) => ({ ...f, description_short: ex.slice(0, MAX_DESC) }))
                        }
                        title="Käytä esimerkkiä"
                      >
                        Käytä
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <textarea
                name="description_short"
                value={form.description_short}
                onChange={onChange}
                maxLength={MAX_DESC}
                placeholder="Kirjoita tähän…"
                className="w-full h-36 rounded-lg px-3 py-2 bg-white dark:bg-[#0b0f1a] text-black/80 dark:text-white/90 border border-black/10 dark:border-white/10 text-xs md:text-lg"
              />
              <div
                className="text-right text-xs md:text-lg text-black/60 dark:text-white/60 mt-1"
                aria-live="polite"
              >
                {form.description_short.length}/{MAX_DESC}
              </div>
            </section>
          )}

          {/* STEP 3: Profiilikuva */}
          {step === 3 && (
            <section>
              <h2 className="font-customFont2 dark:text-white/90 text-black/90 text-2xl font-semibold">
                Profiilikuva
              </h2>

              <p className="mt-2 dark:text-white/80 text-black/80 font-customFont1 text-xs md:text-lg pb-2">
                Lisää näyttävä pääkuva (näkyy listauksissa ja profiilissa). Voit päivittää kuvan milloin vain hallintapaneelissa.
              </p>

              <ProfileImageUploader
                key={(form.profile_image || "none")}
                value={(form.profile_image || "").trim()}
                onChange={(url) => setForm((f) => ({ ...f, profile_image: url }))}
                disabled={saving}
              />
            </section>
          )}

          {/* STEP 4: Tarkistus */}
          {step === 4 && (
            <section>
              <ChecklistView checklist={checklist} />

                  {isAddingAnother ? (
                    <>
                      <div className="mt-3 rounded-md border border-amber-300/60 bg-amber-100/70 dark:bg-amber-900/30 dark:border-amber-800/60 p-3 dark:text-white/80 text-black/80 font-customFont1 text-xs md:text-lg">
                        Kun painat <b>Valmis</b>, siirryt hallintapaneeliin, jossa voit rauhassa viimeistellä uuden esiintyjäkorttisi.
                        Täyttele tiedot vanhasta muistista. Jos jokin kohta mietityttää,{" "}
                        <Link
                          to="/ukk-esiintyjille"
                          className="underline font-semibold"
                          aria-label="Esiintyjien UKK – ohjeet kortin täyttämiseen"
                        >
                          Esiintyjien UKK
                        </Link>{" "}
                        auttaa askel askeleelta.
                      </div>

                      {/* Virhetila: julkaisun tarkistusta ei saatu haettua */}
                      <div className="dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm mt-4">
                        <p className="dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm">
                          Nappuloiden yläpuolella näkyvä tarkistaja listaa puuttuvat kohdat tuttuun tapaan ennen julkaisua.
                        </p>
                      </div>
                    </>
                  ) : (
                <>
                  <p className="mt-3 text-xs md:text-lg text-black/80 dark:text-white/80">
                    Profiilin sisältö ei ratkaise näkyvyyttä, vaan sen, valitaanko juuri sinut/teidät.
                    <br /><br />
                    Täytä huolellisesti kuvaus, musiikkityylit, tarjoamasi palvelut, media ja some-kanavat, ja määritä hinnoittelu harkiten. Panosta laatuun ja konkretiaan: napakka teksti, toimivat kuvat/ääninäytteet ja selkeä palvelukuvaus kasvattavat todennäköisyyttä, että asiakas ottaa yhteyttä ja buukkaa sinut.
                  </p>


                  <p className="mt-3 text-xs md:text-lg text-black/80 dark:text-white/80">
                    Mitä enemmän laadukasta promomatskua (videot ja audioraiteet), sitä paremmin sinut
                    huomataan. Pidä kalenteri ajantasalla – alustan tarkoitus on buukkaukset, ei pelkkä
                    näkyvyys.
                  </p>
                </>
              )}
            </section>
          )}

          {/* Footer-napit */}
          <div className="mt-6 flex items-center justify-between">
            {showBack ? (
              <NeonButton
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                    if (step === 0) {
                        nav.toDashboardUser(navigate, true);   // “Peruuta” vie käyttäjänäkymään
                    } else {
                        prev();
                    }
                    }}

              >
                {step === 0 ? "Peruuta" : "Takaisin"}
              </NeonButton>
            ) : (
              <span />
            )}

            <NeonButton
              type="button"
              variant="primary"
              size="md"
              onClick={next}
              disabled={disableNext}
              className="min-w-40"
            >
              {step < 4 ? (saving ? "Tallennetaan…" : "Seuraava") : "Valmis"}
            </NeonButton>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------- apukomponentit ------- */

function Field({ label, children }) {
  return (
    <div>
      <label className="block font-customFont1 text-sm uppercase tracking-wide text-black/70 dark:text-white/80 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function ChecklistView({ checklist }) {
  if (!checklist) return null;
  const labelMap = {
    name: "Nimi",
    slug: "Slug",
    artist_type: "Esiintyjätyyppi",
    artist_type_name: "Esiintyjätyyppi",
    location: "Paikkakunta",
    description: "Kuvaus",
    description_short: "Lyhyt kuvaus",
    profile_image: "Profiilikuva",
  };
  const items =
    checklist.items ||
    (checklist.missing || []).map((key) => ({
      key,
      ok: false,
      label: labelMap[key] || key,
    }));
  const ok = !!checklist.ok;

  return (
    <div
      className={`rounded-xl p-4 ring-1 ${
        ok
          ? "bg-emerald-100/60 dark:bg-emerald-900/20 ring-emerald-300/60 dark:ring-emerald-700/50"
          : "bg-yellow-100/70 dark:bg-yellow-900/30 ring-yellow-300/60 dark:ring-yellow-800/60"
      }`}
    >
      <h3 className="font-semibold mb-2 text-black/90 dark:text-white/90 text-2xl">
        {ok ? "Kaikki valmista! 🥳" : "Jäi puuttumaan vielä:"}
      </h3>
      <ul className="list-disc pl-5 space-y-1 text-black/80 dark:text-white/80 text-sm">
        {(items || []).map((it) => (
          <li key={it.key}>
            <span className={it.ok ? "opacity-60 line-through" : ""}>{it.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

